import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { api, FalhaApi, type EmpresaApi } from "../api";
import { Layout } from "../componentes/Layout";
import { useSessao } from "../estado/sessao";

export function Perfil() {
  const { usuario } = useSessao();
  const { sair } = useSessao();
  const admin = usuario?.papel === "ADMIN";
  const [aba, setAba] = useState<"perfil" | "empresa">("perfil");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  async function enviar(e: FormEvent) {
    e.preventDefault(); setErro("");
    if (novaSenha !== confirmacao) return setErro("A confirmação da senha não confere.");
    setSalvando(true);
    try {
      await api.trocarSenha(senhaAtual, novaSenha);
      await sair();
    } catch (e) { setErro(e instanceof FalhaApi ? e.message : "Não foi possível alterar a senha"); }
    finally { setSalvando(false); }
  }

  return <Layout><main className="container" style={{ maxWidth: 620 }}>
    <span className="eyebrow">CONTA</span><h2 style={{ marginTop: 4 }}>Perfil e senha</h2>

    {admin && (
      <div className="controle-segmentado" style={{ marginTop: 16 }} aria-label="Seção do perfil">
        <button type="button" className={aba === "perfil" ? "ativo" : ""} onClick={() => setAba("perfil")}>Perfil</button>
        <button type="button" className={aba === "empresa" ? "ativo" : ""} onClick={() => setAba("empresa")}>Empresa</button>
      </div>
    )}

    {aba === "perfil" ? (
      <>
        <div className="cartao" style={{ padding: 18, marginTop: 18 }}>
          <strong>{usuario?.nome}</strong><p style={{ margin: "4px 0 0", color: "var(--texto-suave)", fontSize: 13 }}>{usuario?.email} · {usuario?.papel === "ADMIN" ? "Administrador" : "Vistoriador"}</p>
        </div>
        <form className="cartao" style={{ padding: 18, marginTop: 16 }} onSubmit={(e) => void enviar(e)}>
          <h3>Alterar senha</h3><p style={{ color: "var(--texto-suave)", fontSize: 13 }}>Ao alterar, você será desconectado de todos os dispositivos.</p>
          {erro && <div className="alerta alerta-erro">{erro}</div>}
          <label htmlFor="senha-atual">Senha atual</label><input id="senha-atual" type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} required />
          <label htmlFor="nova-senha">Nova senha</label><input id="nova-senha" type="password" minLength={10} value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} required />
          <label htmlFor="confirmar-senha">Confirmar nova senha</label><input id="confirmar-senha" type="password" minLength={10} value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} required />
          <button className="botao botao-primario" type="submit" disabled={salvando}>{salvando ? "Alterando…" : "Alterar senha"}</button>
        </form>
      </>
    ) : (
      <SecaoEmpresa />
    )}
  </main></Layout>;
}

function SecaoEmpresa() {
  const [empresa, setEmpresa] = useState<EmpresaApi | null>(null);
  const [nome, setNome] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [endereco, setEndereco] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState("");
  const [site, setSite] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    api.obterEmpresa().then(({ empresa }) => {
      setEmpresa(empresa);
      setNome(empresa.nome);
      setCnpj(empresa.cnpj ?? "");
      setEmail(empresa.email ?? "");
      setTelefone(empresa.telefone ?? "");
      setEndereco(empresa.endereco ?? "");
      setCidade(empresa.cidade ?? "");
      setEstado(empresa.estado ?? "");
      setSite(empresa.site ?? "");
    }).catch(() => setErro("Não foi possível carregar os dados da empresa"));
  }, []);

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    setErro(""); setSucesso(false); setSalvando(true);
    try {
      const { empresa } = await api.editarEmpresa({ nome, cnpj, email, telefone, endereco, cidade, estado, site });
      setEmpresa(empresa);
      setSucesso(true);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Não foi possível salvar os dados da empresa");
    } finally {
      setSalvando(false);
    }
  }

  async function trocarLogo(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo) return;
    setErro(""); setEnviandoLogo(true);
    try {
      const { empresa } = await api.enviarLogoEmpresa(arquivo);
      setEmpresa(empresa);
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Não foi possível enviar a logo");
    } finally {
      setEnviandoLogo(false);
    }
  }

  if (!empresa) return <p style={{ marginTop: 18 }}>Carregando…</p>;

  return (
    <>
      <div className="cartao" style={{ padding: 18, marginTop: 18, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 64, height: 64, borderRadius: 12, background: "var(--fundo-secundario, #eef2f7)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {empresa.logoPath ? (
            <img src={`/api/empresa-logo/${empresa.logoPath}`} alt="Logo da empresa" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <span style={{ color: "var(--texto-suave)", fontSize: 12 }}>Sem logo</span>
          )}
        </div>
        <div>
          <label className="botao botao-texto" style={{ marginTop: 0, cursor: "pointer", display: "inline-block" }}>
            {enviandoLogo ? "Enviando…" : "Alterar logo"}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void trocarLogo(e)} disabled={enviandoLogo} style={{ display: "none" }} />
          </label>
          <p style={{ margin: "4px 0 0", color: "var(--texto-suave)", fontSize: 12 }}>PNG, JPG ou WebP, até 3 MB. Aparece no relatório de vistoria.</p>
        </div>
      </div>

      <form className="cartao" style={{ padding: 18, marginTop: 16 }} onSubmit={(e) => void salvar(e)}>
        <h3 style={{ marginTop: 0 }}>Dados da empresa</h3>
        <p style={{ color: "var(--texto-suave)", fontSize: 13, marginTop: -8 }}>Usados no cabeçalho do relatório de vistoria.</p>
        {erro && <div className="alerta alerta-erro">{erro}</div>}
        {sucesso && <div className="alerta alerta-sucesso">Dados salvos.</div>}

        <label htmlFor="e-nome">Nome</label>
        <input id="e-nome" value={nome} onChange={(ev) => setNome(ev.target.value)} required />

        <label htmlFor="e-endereco">Endereço</label>
        <input id="e-endereco" value={endereco} onChange={(ev) => setEndereco(ev.target.value)} />

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="e-cidade">Cidade</label>
            <input id="e-cidade" value={cidade} onChange={(ev) => setCidade(ev.target.value)} />
          </div>
          <div style={{ width: 80 }}>
            <label htmlFor="e-estado">UF</label>
            <input id="e-estado" value={estado} maxLength={2} onChange={(ev) => setEstado(ev.target.value.toUpperCase())} />
          </div>
        </div>

        <label htmlFor="e-cnpj">CNPJ</label>
        <input id="e-cnpj" value={cnpj} onChange={(ev) => setCnpj(ev.target.value)} />

        <label htmlFor="e-email">E-mail</label>
        <input id="e-email" type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} />

        <label htmlFor="e-telefone">Telefone</label>
        <input id="e-telefone" value={telefone} onChange={(ev) => setTelefone(ev.target.value)} />

        <label htmlFor="e-site">Site</label>
        <input id="e-site" value={site} onChange={(ev) => setSite(ev.target.value)} />

        <button className="botao botao-primario" type="submit" disabled={salvando}>{salvando ? "Salvando…" : "Salvar"}</button>
      </form>
    </>
  );
}
